import { inject, injectable, interfaces } from 'inversify';
import { computed } from 'mobx';
import { IAnnotationService } from './annotation.service';
import { ISegmentation } from './annotations.interface';
import { IAnnotationsStore } from './annotations.store';
import { DrawBrush } from './components/DrawControl.models/Brush/Draw.Brush';
import { IDrawer } from './components/DrawControl.models/IDrawer';
import { IFreeDrawSegmentationService } from './freeDrawSegmentation.service';
import { AddBrushBlobCommand } from './undoRedoCommands/addBrushBlobCommand';
import { AnnotationCommand } from './undoRedoCommands/annotationCommand';
import { ChangeSegmentationWithAnswersCommand } from './undoRedoCommands/changeSegmentationWithAnswersCommand';
import { AddVerticesCommandBase, ChangeStateCommand, IUndoRedoCommand } from './undoRedoCommands/undoRedoCommands';
import { IUndoRedoHistoryQueue } from './undoRedoHistoryQueue.service';

export interface IUndoRedoHistory {
  lastUndoCommand: IUndoRedoCommand | undefined;
  canUndo: boolean;
  canRedo: boolean;
  addCommand(command: IUndoRedoCommand): void;
  addNewChangeStateCommand(segmentations: ISegmentation[]): void;
  getNewChangeStateCommand(): ChangeStateCommand;
  clearDrawingHistory(): void;
  clearHistory(): void;
  refreshCommandsBindings(activeAnnotationTypeId: string | undefined, drawer: IDrawer): void;
  undo(): void;
  redo(): void;
}

export namespace IUndoRedoHistory {
  export const $: interfaces.ServiceIdentifier<IUndoRedoHistory> = Symbol('UNDO_REDO_HISTORY_TYPE');
}

@injectable()
export class UndoRedoHistory implements IUndoRedoHistory {
  private lastSegmentations: ISegmentation[] = [];

  constructor(
    @inject(IAnnotationService.$) private readonly annotationService: IAnnotationService,
    @inject(IAnnotationsStore.$) private readonly annotationsStore: IAnnotationsStore,
    @inject(IFreeDrawSegmentationService.$) private readonly freeDrawSegmentationService: IFreeDrawSegmentationService,
    @inject(IUndoRedoHistoryQueue.$) private readonly undoRedoHistoryQueue: IUndoRedoHistoryQueue
  ) {
    this.saveState();
  }

  @computed
  get canRedo(): boolean {
    return this.undoRedoHistoryQueue.canRedo;
  }

  @computed
  get canUndo(): boolean {
    return this.undoRedoHistoryQueue.canUndo;
  }

  get lastUndoCommand(): IUndoRedoCommand | undefined {
    return this.undoRedoHistoryQueue.getLastUndoCommand();
  }

  get nextRedoCommand(): IUndoRedoCommand | undefined {
    return this.undoRedoHistoryQueue.getNextRedoCommand();
  }

  addCommand(command: IUndoRedoCommand): void {
    const lastUndoCommand = this.undoRedoHistoryQueue.getLastUndoCommand();
    if (command instanceof AddVerticesCommandBase && lastUndoCommand instanceof AddVerticesCommandBase) {
      lastUndoCommand.addVertex(command.latlng[0]);
      return;
    }

    if (command instanceof ChangeSegmentationWithAnswersCommand) {
      this.clearDrawingHistory();
    }

    this.undoRedoHistoryQueue.addCommand(command);
    this.saveState();
  }

  addNewChangeStateCommand(segmentations: ISegmentation[]): void {
    const command = new ChangeStateCommand(
      this.annotationService,
      this.annotationsStore,
      this.freeDrawSegmentationService,
      this.annotationService.getSegmentations(),
      segmentations,
      (segmentations: ISegmentation[]) => {
        this.annotationService.setSegmentations(segmentations);
      }
    );
    this.undoRedoHistoryQueue.addCommand(command);
    this.saveState();
  }

  getNewChangeStateCommand(): ChangeStateCommand {
    return new ChangeStateCommand(
      this.annotationService,
      this.annotationsStore,
      this.freeDrawSegmentationService,
      this.lastSegmentations,
      this.annotationService.getSegmentations(),
      (segmentations: ISegmentation[]) => {
        this.annotationService.setSegmentations(segmentations);
      }
    );
  }

  clearDrawingHistory(): void {
    this.undoRedoHistoryQueue.removeCommands(this.isDrawingCommand);
  }

  clearHistory(): void {
    this.lastSegmentations = [];
    this.undoRedoHistoryQueue.removeCommands(() => true);
    this.saveState();
  }

  refreshCommandsBindings(activeAnnotationTypeId: string | undefined, drawer: IDrawer): void {
    this.undoRedoHistoryQueue.changeCommands((command: IUndoRedoCommand) => {
      if (command instanceof AddBrushBlobCommand && command.brush.activeAnnotationTypeId === activeAnnotationTypeId) {
        command.brush = drawer as DrawBrush;
      }
    });
  }

  undo(): void {
    if (!this.undoRedoHistoryQueue.canUndo) {
      return;
    }
    this.undoRedoHistoryQueue.undo();
    this.saveState();
  }

  redo(): void {
    if (!this.undoRedoHistoryQueue.canRedo) {
      return;
    }
    this.undoRedoHistoryQueue.redo();
    this.saveState();
  }

  private saveState(): void {
    this.lastSegmentations = this.annotationService.getSegmentations();
  }

  isDrawingCommand = (command: IUndoRedoCommand | undefined): boolean => {
    return command !== undefined && !(command instanceof AnnotationCommand);
  };
}
